import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxHailuo02Params {
  /**
   * 视频生成提示词，长度必须在1-2000个字符之间
   */
  prompt: string;
  /**
   * 视频时长，支持6秒（所有分辨率）或10秒（仅512P/768P）
   * @values 6 | 10
   */
  duration?: 6 | 10;
  /**
   * 视频分辨率，默认为768P
   * @values 512P | 768P | 1080P
   */
  resolution?: '512P' | '768P' | '1080P';
  /**
   * 是否启用提示词优化器，默认为true
   */
  prompt_optimizer?: boolean;
  /**
   * 是否启用快速预处理，默认为false
   */
  fast_pretreatment?: boolean;
}

export interface MinimaxHailuo02Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_hailuo_02
 *
 * MiniMax Hailuo-02 文本生成视频接口
 * 
 * Model: MiniMax-Hailuo-02 (固定模型)
 * Features: 文本生成视频功能，支持摄像机控制
 * Duration: 6秒（所有分辨率）或10秒（仅512P/768P）
 * Resolution: 512P、768P（默认）、1080P
 * Advanced: 支持提示词优化器（默认开启）、快速预处理、回调URL通知
 * 
 * API Documentation: https://www.minimax.io/platform/document/text_to_video?key=68abdfffd08627aad9674bff
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxHailuo02 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxHailuo02({
 *   // 参数见 MinimaxHailuo02Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxHailuo02Params 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxHailuo02(
  params: MinimaxHailuo02Params,
): Promise<MinimaxHailuo02Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_hailuo_02',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxHailuo02Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
